import React, { useEffect, useMemo, useState } from 'react';
import { useFetchPovItemsForProduct } from '../../../../hooks/opportunity-pov-hooks';
import StbButton from '../../../security-toolbox-ui/StbButton';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import {
  FormComponentType,
  useTestCaseFormContext,
} from './ValidationTestCaseForm/ValidationTestCaseFormContext';
import FormSectionOutcome from './ValidationTestCaseForm/FormSectionOutcome';
import { OpportunityProduct } from '../../../../types/opportunity';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity } from '../../../../hooks/opportunity-hooks';

const ValidatePhaseTestCases: React.FC<{
  selectedProductId: OpportunityProduct['id'];
  isActive: boolean;
}> = ({ selectedProductId, isActive }) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);

  const {
    setForm,
    resetForm,
    getFormIdsForType,
    addFormItem,
    opportunityProduct,
  } = useTestCaseFormContext();

  const { data, isFetching, isLoading, refetch } = useFetchPovItemsForProduct(
    opportunityId,
    selectedProductId,
    { enabled: !!opportunity && !!selectedProductId }
  );

  const [formInitialized, setFormInitialized] = useState(false);

  const criteriaFeatureLookup = useMemo(() => {
    const lookup = new Map<number, { feature_name: string; feature_id: number }>();

    const productUseCases =
      (opportunityProduct?.product as any)?.use_case ||
      (opportunityProduct?.product as any)?.product_use_case ||
      (opportunityProduct?.product as any)?.product_use_cases ||
      [];

    productUseCases.forEach((useCase: any) => {
      const features = useCase?.product_feature || [];
      features.forEach((feature: any) => {
        const criteria = feature?.product_feature_success_criteria || [];
        criteria.forEach((criterion: any) => {
          lookup.set(criterion.id, {
            feature_name: feature.name || feature.title || '',
            feature_id: feature.id,
          });
        });
      });
    });

    return lookup;
  }, [opportunityProduct]);

  const handleAddOutcomeButtonClick = () => {
    addFormItem(FormComponentType.Outcome, undefined);
  };

  useEffect(() => {
    if (!selectedProductId) {
      setFormInitialized(false);
      return;
    }

    if (isLoading || isFetching) {
      return;
    }

    const safeData = Array.isArray(data) ? data : [];

    const items = safeData.map(({ usecases, ...outcome }) => ({
      ...outcome,
      type: FormComponentType.Outcome,
      usecases,
      children: (usecases ?? []).map((u) => `${FormComponentType.UseCase}-${u.id}`),
    }));

    const usecases = items.flatMap((item) =>
      (item.usecases ?? []).map(({ testcases, outcome_id, ...usecase }) => ({
        ...usecase,
        product_use_case: usecase.product_use_case ?? null,
        is_custom: usecase.is_custom ?? !usecase.product_use_case,
        type: FormComponentType.UseCase,
        testcases,
        children: (testcases ?? []).map((t) => `${FormComponentType.TestCase}-${t.id}`),
        parent: `${FormComponentType.Outcome}-${outcome_id}`,
      }))
    );

    const testcases = usecases.flatMap((uc) =>
      (uc.testcases ?? []).map(({ usecase_id, ...testcase }) => {
        const criteriaId = testcase.product_feature_success_criteria ?? null;
        const featureMeta = criteriaId ? criteriaFeatureLookup.get(criteriaId) : undefined;

        return {
          ...testcase,
          product_feature_success_criteria: criteriaId,
          is_custom: testcase.is_custom ?? !criteriaId,
          feature_name: featureMeta?.feature_name || '',
          feature_id: featureMeta?.feature_id || null,
          type: FormComponentType.TestCase,
          parent: `${FormComponentType.UseCase}-${usecase_id}`,
        };
      })
    );

    setForm([...items, ...usecases, ...testcases]);
    setFormInitialized(true);
  }, [data, isFetching, isLoading, selectedProductId, setForm, criteriaFeatureLookup]);

  useEffect(() => {
    resetForm();
    setFormInitialized(false);
  }, [selectedProductId, resetForm]);

  useEffect(() => {
    if (isActive && selectedProductId) {
      refetch();
    }
  }, [isActive, selectedProductId, refetch]);

  const outcomeIds = getFormIdsForType(FormComponentType.Outcome) ?? [];
  const showLoading = !!selectedProductId && !formInitialized && (isLoading || isFetching);

  return (
    <div className="hbr-css__layout-col-xs">
      <StbButton
        role="add"
        size="medium"
        variant="text"
        onClick={handleAddOutcomeButtonClick}
        className="flex justify-end"
      >
        Add Outcome
      </StbButton>

      {!selectedProductId ? (
        <span className="text-sm font-normal italic">Select a product to view outcomes</span>
      ) : showLoading ? (
        <HbrSpinner size="medium">
          <div slot="label">Loading...</div>
        </HbrSpinner>
      ) : outcomeIds.length === 0 ? (
        <span className="text-sm font-normal italic">No outcomes found</span>
      ) : (
        outcomeIds.map((id) => <FormSectionOutcome formSectionId={id} key={id} />)
      )}
    </div>
  );
};

export default ValidatePhaseTestCases;